import { Injectable } from '@angular/core';
import 'rxjs/add/operator/map';
import * as firebase from 'firebase';
import { FirestoreProvider } from '../firestore/firestore.provider';
import { SessionStorageProvider } from '../../session-storage/session-storage.provider';
import { Subject } from 'rxjs/Subject';

/**
 * Provider to handle the Firebase Authentication actions
 */
@Injectable()
export class AuthProvider {
  private currentUser: firebase.User;
  authSubject: Subject<boolean> = new Subject;

  constructor(
    public firestoreProvider: FirestoreProvider,
    public sessionStorageProvider: SessionStorageProvider
  ) {
    firebase.auth().onAuthStateChanged((user: firebase.User) => {
      this.currentUser = user;
      if (this.currentUser) {
        this.storageUserToken();
        this.authSubject.next(true);
      } else {
        this.sessionStorageProvider.removeItem('currentUser-token');
        this.authSubject.next(false);
      }
    });
  }

  /**
   * Returns Auth object from Firebase
   */
  storageUserToken() {
    this.currentUser.getIdToken().then(token => {
      this.sessionStorageProvider.setItem('currentUser-token', token);
    });
  }

  /**
   * Get firebase's current auth object
   */
  getAuth() {
    return firebase.auth();
  }

  /**
   * Checks if an user is authenticated on session.
   */
  isAuthenticated() {
    return this.currentUser != null;
  }

  /**
   * Get current user's UID
   */
  getUserId() {
    return this.currentUser.uid;
  }

  /**
   * Gets current user's name
   */
  getUserName() {
    return this.currentUser.displayName;
  }

  /**
   * Gets current user's email
   */
  getUserEmail() {
    return this.currentUser.email;
  }

  /**
   * Authenticates user with email and password
   * @param email
   * @param password
   */
  signInWithEmailAndPassword(email: string, password: string) {
    return firebase.auth().signInWithEmailAndPassword(email, password);
  }

  /**
   * Creates user with email and password
   * @param email
   * @param password
   */
  signUpWithEmailAndPassword(email: string, password: string) {
    return firebase.auth().createUserWithEmailAndPassword(email, password);
  }

  /**
   * Change current user's email
   * @param email 
   */
  changeEmail(email: string) {
    return firebase.auth().currentUser.updateEmail(email);
  }

  /**
   * Changes current user's password
   * @param password 
   */
  changePassword(password: string) {
    return firebase.auth().currentUser.updatePassword(password);
  }

  /**
   * Reauthenticates current user
   * @param credential 
   */
  reAuthenticateUser(credential: firebase.auth.AuthCredential) {
    return firebase.auth().currentUser.reauthenticateAndRetrieveDataWithCredential(credential);
  }

  /**
   * Creates current user's credential
   * @param email 
   * @param password 
   */
  createEmailCredential(email: string, password: string) {
    return firebase.auth.EmailAuthProvider.credential(email, password);
  }

  /**
   * Updates current user profile
   * @param profile user's profile
   */
  updateUserProfile(profile: any) {
    return this.currentUser.updateProfile(profile);
  }

  /**
   * Resets current user's password
   * @param email user's email
   */
  resetPassword(email: string) {
    return firebase.auth().sendPasswordResetEmail(email);
  }

  /**
   * Signs current user out
   */
  signOut() {
    return firebase.auth().signOut();
  }
}
